import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import { addOrUpdateTemplate, getSharedTemplate, getTemplate } from './api';
import { DOCUMENT_SIZE_DIMENSIONS } from './document-size';
import { messages } from './use-document-template-messages';

import type { DocumentComponents, DocumentSize, DocumentTemplateType, PageDefinition } from './config';

const getInitialDefinition = (documentSize: DocumentSize): PageDefinition => {
  const size = DOCUMENT_SIZE_DIMENSIONS[documentSize];

  return {
    data: {
      width: size.width,
      height: size.height,
      displayMode: 'grid',
    },
    ui: {
      documentSize,
      gridCellSize: size.gridCellSize,
    },
  };
};

type DocumentTemplate = {
  page: PageDefinition;
  updatePage: (updated: Partial<PageDefinition>) => void;
  components: DocumentComponents;
  setComponents: React.Dispatch<React.SetStateAction<DocumentComponents>>;
  saveDocument: () => void;
  isSaving: boolean;
  resetDocument: () => void;
  isLoading: boolean;
};

type UseDocumentTemplateOptions = {
  onLoad?: (page: PageDefinition, components: DocumentComponents) => void;
};

export const useDocumentTemplate = (
  documentType: DocumentTemplateType,
  documentSize: DocumentSize,
  options?: UseDocumentTemplateOptions,
): DocumentTemplate => {
  const [page, setPage] = useState<PageDefinition>(getInitialDefinition(documentSize));
  const [components, setComponents] = useState<DocumentComponents>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { formatMessage } = useIntl();

  useEffect(() => {
    const loadTemplate = async () => {
      setIsLoading(true);
      try {
        const definition = await getTemplate(documentType);
        if (definition) {
          const size = DOCUMENT_SIZE_DIMENSIONS[documentSize];
          const loadedPage = {
            data: definition.page,
            ui: {
              documentSize,
              gridCellSize: size.gridCellSize,
            },
          };
          setPage(loadedPage);
          setComponents(definition.components);
          options?.onLoad?.(loadedPage, definition.components);
        } else {
          const sharedDefinition = await getSharedTemplate(documentType);
          if (sharedDefinition) {
            const size = DOCUMENT_SIZE_DIMENSIONS[documentSize];
            const loadedPage = {
              data: sharedDefinition.page,
              ui: {
                documentSize,
                gridCellSize: size.gridCellSize,
              },
            };
            setPage(loadedPage);
            setComponents(sharedDefinition.components);
            options?.onLoad?.(loadedPage, sharedDefinition.components);
          }
        }
      } catch {
        const initialPage = getInitialDefinition(documentSize);
        const initialComponents: DocumentComponents = [];
        setPage(initialPage);
        setComponents(initialComponents);
        options?.onLoad?.(initialPage, initialComponents);
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplate();
  }, [documentType, documentSize, options]);

  const saveDocument = useCallback(async () => {
    try {
      setIsSaving(true);
      await addOrUpdateTemplate(documentType, {
        page: page.data,
        components,
      });
      NotificationService.showNotification({
        title: formatMessage(messages.saveSuccessTitle),
        content: formatMessage(messages.saveSuccessMessage),
        notificationType: NOTIFICATION_TYPE_SUCCESS,
      });
    } catch {
      NotificationService.showNotification({
        title: formatMessage(messages.saveErrorTitle),
        content: formatMessage(messages.saveErrorMessage),
        notificationType: NOTIFICATION_TYPE_ERROR,
      });
    } finally {
      setIsSaving(false);
    }
  }, [documentType, page.data, components, formatMessage]);

  const resetDocument = useCallback(async () => {
    try {
      setIsLoading(true);
      const sharedTemplate = await getSharedTemplate(documentType);
      const size = DOCUMENT_SIZE_DIMENSIONS[documentSize];
      setPage({
        data: sharedTemplate.page,
        ui: {
          documentSize,
          gridCellSize: size.gridCellSize,
        },
      });
      setComponents(sharedTemplate.components);
      NotificationService.showNotification({
        title: formatMessage(messages.resetSuccessTitle),
        content: formatMessage(messages.resetSuccessMessage),
        notificationType: NOTIFICATION_TYPE_SUCCESS,
      });
    } catch {
      NotificationService.showNotification({
        title: formatMessage(messages.resetErrorTitle),
        content: formatMessage(messages.resetErrorMessage),
        notificationType: NOTIFICATION_TYPE_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  }, [documentType, documentSize, formatMessage]);

  const updatePage = useCallback((updated: Partial<PageDefinition>) => {
    setPage((prev) => ({
      data: { ...prev.data, ...updated.data },
      ui: { ...prev.ui, ...updated.ui },
    }));
  }, []);

  return {
    page,
    updatePage,
    components,
    setComponents,
    saveDocument,
    isSaving,
    resetDocument,
    isLoading,
  };
};
